<template>
  <div
    ref="root"
    class="tabs border-b border-b-border relative text-size-6 desktop:text-size-5 overflow-auto tablet:border-b-0 desktop:overflow-visible"
  >
    <div class="desktop:container desktop:border-b desktop:border-b-border">
      <ul class="inline-flex gap-4 desktop:gap-6">
        <li
          v-for="tab in mapped"
          :id="'tab-' + tab.id"
          ref="elements"
          :key="tab.id"
          class="desktop:!px-0"
          :class="{ 'is-active': active === tab.id }"
        >
          <Component
            :is="tab.tagName"
            v-bind="tab.attributes"
            class="font-bold text-muted py-2 desktop:py-4 whitespace-nowrap"
            :class="{
              'text-scheme-invert': active === tab.id,
              'hover:text-scheme-invert-ter': active !== tab.id,
            }"
            :style="tab.id === active ? activeToggleStyle : undefined"
            @click.prevent="$emit('click', tab.id)"
            >{{ tab.label }}</Component
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useDebounceFn } from '@vueuse/core'
import type { TabItem } from './types'

const props = defineProps<{
  tabs: TabItem[]
  active?: string
  isLink?: boolean
}>()

defineEmits<{
  click: [string]
}>()

const { isUntilDesktop } = useViewport()

const root = ref<HTMLElement>()
const elements = ref<HTMLLIElement[]>([])

const scrollIntoView = useDebounceFn(() => {
  if (!isUntilDesktop.value || !root.value) {
    return
  }

  const element = elements.value.find((v) => v.id === 'tab-' + props.active)
  if (!element) {
    return
  }

  const container = root.value
  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()

  const isFullyVisible =
    elementRect.left >= containerRect.left &&
    elementRect.right <= containerRect.right

  if (isFullyVisible) {
    return
  }

  const scrollAmount = elementRect.left - containerRect.left
  container.scroll({
    left: container.scrollLeft + scrollAmount,
    behavior: 'smooth',
  })
}, 100)

watch(() => props.active, scrollIntoView)

const mapped = computed(() => {
  return props.tabs.map((tab) => {
    return {
      ...tab,
      tagName: props.isLink ? 'a' : 'button',
      attributes: props.isLink ? { href: '#' + tab.id } : {},
    }
  })
})

const activeToggleStyle: Record<string, string> = {
  'anchor-name': '--tabs-anchor',
}
</script>

<style lang="postcss">
.tabs {
  ul {
    li:first-child {
      padding-left: var(--container-spacer);
    }
    li:last-child {
      padding-right: var(--container-spacer);
    }
  }
  &:after {
    content: '';
    @apply absolute bottom-0 left-0 pointer-events-none h-[0.2em] bg-scheme-invert;
    @apply transition-all duration-300 ease-swing;
    position-anchor: --tabs-anchor;
    left: anchor(left);
    width: anchor-size(width);
  }
}
</style>
